import { useEffect, useState } from "react";
import { http, formatApiError } from "../lib/api";
import { useNavigate } from "react-router-dom";
import { Wand2, X } from "lucide-react";
import { DateInput } from "../components/DateInput";
import { SearchSelect } from "../components/SearchSelect";

export default function GenerateDevisDialog({ onClose }) {
  const today = new Date().toISOString().slice(0, 10);
  const in30 = new Date(Date.now() + 30 * 86400e3).toISOString().slice(0, 10);
  const [v, setV] = useState({ start: today, end: in30, site_id: "", agent_id: "", client_name: "", client_email: "", group_by_type: true, apply_min_floor: true });
  const [sites, setSites] = useState([]);
  const [agents, setAgents] = useState([]);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    Promise.all([http.get("/sites"), http.get("/agents")]).then(([s, a]) => { setSites(s.data); setAgents(a.data); });
  }, []);

  const submit = async () => {
    setLoading(true); setErr("");
    try {
      const body = { ...v };
      if (!body.site_id) delete body.site_id;
      if (!body.agent_id) delete body.agent_id;
      const r = await http.post("/vacations/generate-devis", body);
      onClose();
      nav("/devis");
      setTimeout(()=> alert(`Devis ${r.data.number} généré avec ${r.data.items.length} ligne(s) — total TTC ${r.data.total_ttc} €`), 100);
    } catch (e) { setErr(formatApiError(e)); }
    finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm grid place-items-center p-4">
      <div className="bg-white rounded-md w-full max-w-lg border border-slate-200 animate-fade-in-up" onClick={(e)=>e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <h3 className="font-display text-xl font-bold inline-flex items-center gap-2"><Wand2 className="h-5 w-5 text-sky-600"/> Devis depuis planning</h3>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded"><X className="h-4 w-4"/></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <L label="Du *"><DateInput data-testid="gen-start" className="input" value={v.start} onChange={(e)=>setV({...v, start:e.target.value})}/></L>
            <L label="Au *"><DateInput data-testid="gen-end" className="input" value={v.end} onChange={(e)=>setV({...v, end:e.target.value})}/></L>
          </div>
          <L label="Site (optionnel — toutes si vide)">
            <SearchSelect data-testid="generatedevisdialog-site" className="input" value={v.site_id} onChange={(e)=>setV({...v, site_id:e.target.value})}>
              <option value="">— Tous les sites —</option>
              {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </SearchSelect>
          </L>
          <L label="Agent (optionnel)">
            <select className="input" value={v.agent_id} onChange={(e)=>setV({...v, agent_id:e.target.value})}>
              <option value="">— Tous les agents —</option>
              {agents.map(a => <option key={a.id} value={a.id}>{a.last_name} {a.first_name}</option>)}
            </select>
          </L>
          <L label="Client *"><input data-testid="gen-client" className="input" value={v.client_name} onChange={(e)=>setV({...v, client_name:e.target.value})} placeholder="Nom du client"/></L>
          <L label="Email client"><input className="input" type="email" value={v.client_email} onChange={(e)=>setV({...v, client_email:e.target.value})}/></L>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={v.group_by_type} onChange={(e)=>setV({...v, group_by_type:e.target.checked})}/>
            <span>Grouper par type d'heure (sinon une ligne par vacation)</span>
          </label>
          <label className="flex items-start gap-2 text-sm">
            <input type="checkbox" className="mt-0.5" data-testid="gen-min-floor" checked={v.apply_min_floor !== false} onChange={(e)=>setV({...v, apply_min_floor:e.target.checked})}/>
            <span>Appliquer le plancher minimum de 6 h par vacation <span className="text-xs text-slate-500">(IDCC 1351 — sinon facturation au temps réel, avec mention sur le devis)</span></span>
          </label>
          {err && <div className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">{err}</div>}
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-slate-200 bg-slate-50">
          <button onClick={onClose} className="px-4 py-2 text-sm font-semibold border border-slate-300 rounded-md hover:bg-slate-100">Annuler</button>
          <button data-testid="gen-submit" disabled={loading || !v.client_name} onClick={submit} className="px-4 py-2 text-sm font-semibold bg-sky-600 hover:bg-sky-700 text-white rounded-md disabled:opacity-70 inline-flex items-center gap-1"><Wand2 className="h-4 w-4"/>{loading?"Génération…":"Générer le devis"}</button>
        </div>
      </div>
      <style>{`.input{width:100%;border:1px solid #cbd5e1;border-radius:6px;padding:.55rem .7rem;font-size:.875rem;outline:none;background:white}.input:focus{border-color:#0284c7;box-shadow:0 0 0 3px #0ea5e933}`}</style>
    </div>
  );
}

function L({ label, children }) { return <label className="block"><div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">{label}</div>{children}</label>; }
